import { useEffect, type ReactNode } from 'react';
import { Button } from './Button';

/**
 * Модальное окно.
 *
 * Внутри окна **поповеры не используются** — тело окна прокручивается,
 * и всё `position:absolute` в нём обрезается (CLAUDE.md, «Интерфейс»).
 * Вместо них парный выбор, список вариантов `OptionList` или поле
 * с ручным вводом даты.
 *
 * Подтверждающее действие слева, отмена справа тихой кнопкой —
 * правило кита для форм.
 */

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Подтверждающее действие; если его нет, в подвале только «Закрыть» */
  confirm?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    danger?: boolean;
  };
  cancelLabel?: string;
  /** Ширина: по умолчанию 440 (кит), `wide` — 560, `xl` — 720 (стили модуля M2) */
  size?: 'wide' | 'xl';
  /** Свой подвал вместо «подтвердить / отмена» — когда кнопок больше или она одна */
  footer?: ReactNode;
}

export function Modal({
  title,
  onClose,
  children,
  confirm,
  cancelLabel = 'Отмена',
  size,
  footer,
}: ModalProps) {
  useEffect(() => {
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [onClose]);

  return (
    <div className="ovl" onClick={onClose}>
      <div
        className={size ? `modal ${size}` : 'modal'}
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mh">
          <h4>{title}</h4>
          <span className="cl" onClick={onClose}>
            ✕
          </span>
        </div>

        <div className="mb">{children}</div>

        <div className="mf">
          {footer ?? (
            <>
              {confirm ? (
                <Button
                  tone={confirm.danger ? 'danger' : 'primary'}
                  disabled={confirm.disabled}
                  onClick={confirm.onClick}
                >
                  {confirm.label}
                </Button>
              ) : null}
              <Button tone="quiet" onClick={onClose}>
                {confirm ? cancelLabel : 'Закрыть'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
